import { captureException } from '@sentry/react';
import { exists, UnreachableError } from 'base/helpers';
import { Dialog } from 'base/ui/dialog';
import { FavIcon } from 'base/ui/fav_icon';
import { Rows } from 'base/ui/layout';
import { ProgressBar } from 'base/ui/progress_bar';
import { TextRegular, TextSmall, TextSmallLight } from 'base/ui/typography';
import {
  BlocksService,
  CrawlError,
  CrawlPageEvent,
} from 'blocks/blocks_service';
import {
  aPage,
  aProject,
  Page,
  Project,
  projectDomain,
} from 'blocks/editor/model';
import { SimpleConnection } from 'blocks/woot_woot/base';
import * as React from 'react';
import styles from './sitemap_import_importing_dialog.css';

type CrawlingStatus =
  | {
      kind: 'starting';
    }
  | {
      kind: 'importing';
      currentPageIndex: number;
      totalPages: number;
      pageLimitReached: boolean;
    }
  | {
      kind: 'done';
      totalPages: number;
      pageLimitReached: boolean;
    };

type CrawlSiteResult =
  | {
      kind: 'success';
      pageEvents: readonly CrawlPageEvent[];
    }
  | {
      kind: 'error';
      error: unknown;
    };

type CrawlSiteOpts = {
  blocksService: BlocksService;
  domain: string;
  updateStatus: (newStatus: CrawlingStatus) => void;
};

async function crawlSite({
  blocksService,
  domain,
  updateStatus,
}: CrawlSiteOpts): Promise<CrawlSiteResult> {
  const pageEvents: CrawlPageEvent[] = [];
  let totalPages = 0;
  let pageLimitReached = false;
  try {
    // `sitemapImporterCrawl` will send events reporting the progress and
    // the metadata for every page.
    for await (const event of blocksService.sitemapImporterCrawl(domain)) {
      switch (event.type) {
        case 'status':
          {
            totalPages = event.totalPages;
            pageLimitReached = event.pageLimitReached;
            updateStatus({
              kind: 'importing',
              // TODO (Luisma): rename `event.currentPage` to `event.currentPageIndex` in `blocksService.sitemapImporterCrawl` to indicate the number is 0-based.
              currentPageIndex: event.currentPage,
              totalPages: event.totalPages,
              pageLimitReached: event.pageLimitReached,
            });
          }
          break;
        case 'page':
          pageEvents.push(event);
          break;
        default:
          throw new UnreachableError(event);
      }
    }
  } catch (error) {
    if (!(error instanceof CrawlError)) {
      captureException(error);
    }
    // TODO (Luisma): replace with an errorService
    console.error(error);

    return {
      kind: 'error',
      error,
    };
  }

  updateStatus({
    kind: 'done',
    totalPages,
    pageLimitReached,
  });

  return {
    kind: 'success',
    pageEvents,
  };
}

function formatPageName(pathSegment: string) {
  let name = pathSegment
    .split(/\W|_/)
    .filter((s) => s.trim() !== '')
    .join(' ');
  if (name.length === 0) {
    return '';
  }
  return name[0].toUpperCase() + name.slice(1);
}

type UpdateProjectWithImportedSitemapOpts = {
  project: Project;
  pageEvents: readonly CrawlPageEvent[];
};

function updateProjectWithImportedSitemap({
  project,
  pageEvents,
}: UpdateProjectWithImportedSitemapOpts) {
  const pageMap = new Map<string, Page>();

  for (const event of pageEvents) {
    const pathSegments = event.path
      .split('?')[0]
      .split('/')
      .filter((s) => s !== '');

    let pagePrompt = '';

    // If we have page metadata (title and/or description) we use it for the page prompt.
    const content = [event.title, event.description]
      .filter(exists)
      .map((s) => s.trim())
      .filter((s) => s !== '');

    if (content.length > 0) {
      pagePrompt = [
        ...content,
        'Note: the above information has been pulled from the page title and meta description.',
      ].join('\n\n');
    }

    if (pathSegments.length === 0) {
      // This is the home page.
      project.homePage.name = 'Home';
      project.homePage.description = pagePrompt;
      // The home prompt will be also use as the site prompt
      project.brief = pagePrompt;
    } else {
      // Ensure pages exist for previous path segments.
      let previousPage = project.homePage;
      for (let i = 0; i < pathSegments.length - 1; i++) {
        const key = pathSegments.slice(0, i + 1).join('/');
        if (!pageMap.has(key)) {
          const [p] = previousPage.subPages.push(
            aPage({
              name: formatPageName(pathSegments[i]),
              pageType: 'path',
            }),
          );
          pageMap.set(key, p);
          previousPage = p;
        }
        previousPage = pageMap.get(key)!;
      }

      // Add the actual page.
      const key = pathSegments.join('/');
      const existingPage = pageMap.get(key);
      if (existingPage === undefined) {
        const [p] = previousPage.subPages.push(
          aPage({
            name: formatPageName(pathSegments[pathSegments.length - 1]),
            description: pagePrompt,
          }),
        );
        pageMap.set(key, p);
      } else {
        // Page already exists, probably added as a ghost page above.
        // It'll already have a name, but update its page prompt.
        existingPage.description = pagePrompt;
        existingPage.pageType = 'page';
      }
    }
  }
}

type GenerateProjectOpts = {
  domain: string;
};

function createProject({ domain }: GenerateProjectOpts): Project {
  // This project will contain all the generated pages from the imported site.
  const project = projectDomain.create(
    '',
    aProject({ name: domain }),
    new SimpleConnection(),
  );

  return project;
}

type ImportSitemapResult =
  | {
      kind: 'success';
      domain: string;
      projectToken: string;
    }
  | {
      kind: 'error';
      error: unknown;
    };

type ImportSitemapOpts = {
  blocksService: BlocksService;
  domain: string;
  owner: 'user' | 'team';
  updateStatus: (newStatus: CrawlingStatus) => void;
  /** If provided it will import the site into the given project (assuming is empty).
   * Otherwise it will create a new project. */
  project?: Project;
};

async function importSitemap({
  blocksService,
  domain,
  owner,
  updateStatus,
  project,
}: ImportSitemapOpts): Promise<ImportSitemapResult> {
  // Steps to import a site:
  // 1. Crawl all the pages
  // 2. Create an in-memory project and add all the crawled pages.
  // 3. Create a project in the backend with all the generated state.
  const crawlSiteResult = await crawlSite({
    blocksService,
    domain,
    updateStatus,
  });
  switch (crawlSiteResult.kind) {
    case 'success': {
      if (project != null) {
        updateProjectWithImportedSitemap({
          project,
          pageEvents: crawlSiteResult.pageEvents,
        });

        return {
          kind: 'success',
          domain,
          projectToken: project.oid,
        };
      }

      const newProject = createProject({
        domain,
      });
      updateProjectWithImportedSitemap({
        project: newProject,
        pageEvents: crawlSiteResult.pageEvents,
      });
      try {
        const createProjectResponse = await blocksService.createProject({
          data: newProject.state,
          owner,
        });
        return {
          kind: 'success',
          domain,
          projectToken: createProjectResponse.token,
        };
      } catch (error) {
        return {
          kind: 'error',
          error: new Error('We encountered an issue creating the project'),
        };
      }
    }
    case 'error': {
      return crawlSiteResult;
    }
    default: {
      throw new UnreachableError(crawlSiteResult);
    }
  }
}

type ImportingDialogViewProps = {
  domain: string;
} & (
  | {
      kind: 'starting';
    }
  | {
      kind: 'importing';
      currentPageIndex: number;
      pageLimitReached: boolean;
      totalPages: number;
    }
  | {
      kind: 'done';
      pageLimitReached: boolean;
      totalPages: number;
    }
);

export function ImportingDialogView(props: ImportingDialogViewProps) {
  const progressDescriptionId = React.useId();
  return (
    <Dialog title={undefined}>
      <div className={styles.container}>
        <Rows spacing={16} xalign="center">
          <Rows spacing={16} xalign="center">
            <FavIcon domain={props.domain} size="dynamic" />
            <Rows spacing={4} xalign="center">
              <TextRegular color="black">Importing</TextRegular>
              <TextSmallLight color="grey" lineClamp={1} wordBreak="break-word">
                Sitemap from {props.domain}
              </TextSmallLight>
            </Rows>
            <Rows spacing={4} xalign="center" yalign="center">
              <div className={styles.progressBarWrapper}>
                <ProgressBar
                  ariaDescribedBy={progressDescriptionId}
                  currentStep={
                    props.kind === 'starting'
                      ? 0
                      : props.kind === 'importing'
                      ? props.currentPageIndex + 1
                      : props.totalPages
                  }
                  totalSteps={props.kind === 'starting' ? 5 : props.totalPages}
                  currentStepDurationMs={3000}
                />
              </div>
              <div id={progressDescriptionId}>
                {props.kind === 'importing' || props.kind === 'done' ? (
                  <TextSmallLight color="grey">
                    {/* Using `tabularNums` for a less jarring animation */}
                    <TextSmall color="black" tag="span" tabularNums={true}>
                      {/* Using the 0-based index when "importing" because it represents
                       * the page we're currently _importing_. It hasn't been _imported_ yet. */}
                      {props.kind === 'done'
                        ? props.totalPages
                        : props.currentPageIndex}{' '}
                      of {props.totalPages}
                      {props.pageLimitReached ? ' (max)' : ''}
                    </TextSmall>{' '}
                    pages imported
                  </TextSmallLight>
                ) : (
                  <TextSmallLight color="grey">Calculating...</TextSmallLight>
                )}
              </div>
            </Rows>
          </Rows>
        </Rows>
      </div>
    </Dialog>
  );
}

export type ImportCompletedResult = ImportSitemapResult;

type SitemapImportImportingDialogProps = {
  blocksService: BlocksService;
  importBehaviour:
    | {
        kind: 'import-into-new-project';
      }
    | {
        kind: 'import-into-existing-project';
        project: Project;
      };
  domain: string;
  onImportCompleted: (result: ImportCompletedResult) => void;
  owner: 'user' | 'team';
};

export function SitemapImportImportingDialog({
  blocksService,
  importBehaviour,
  domain,
  onImportCompleted,
  owner,
}: SitemapImportImportingDialogProps) {
  const [status, setStatus] = React.useState<CrawlingStatus>({
    kind: 'starting',
  });
  React.useEffect(() => {
    let cancelled = false;
    importSitemap({
      blocksService,
      domain,
      owner,
      updateStatus: setStatus,
      project:
        importBehaviour.kind === 'import-into-existing-project'
          ? importBehaviour.project
          : undefined,
    }).then((result) => {
      if (!cancelled) {
        onImportCompleted(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [blocksService, domain, owner, importBehaviour, onImportCompleted]);
  switch (status.kind) {
    case 'starting': {
      return <ImportingDialogView kind="starting" domain={domain} />;
    }
    case 'importing': {
      return (
        <ImportingDialogView
          kind="importing"
          domain={domain}
          currentPageIndex={status.currentPageIndex}
          pageLimitReached={status.pageLimitReached}
          totalPages={status.totalPages}
        />
      );
    }
    case 'done': {
      return (
        <ImportingDialogView
          kind="done"
          domain={domain}
          pageLimitReached={status.pageLimitReached}
          totalPages={status.totalPages}
        />
      );
    }
    default: {
      throw new UnreachableError(status);
    }
  }
}
